import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCalendar, FiBookOpen, FiChevronRight, FiStar } from 'react-icons/fi';
import './Magazine.css';

const Magazine = () => {
  const slugify = (text) => {
    if (!text) return '';
    return text
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const getArticleSlug = (article) => {
    if (article?.slug) return article.slug;
    const base = slugify(article?.title) || 'articulo';
    return article?.id ? `${base}-${article.id}` : base;
  };
  const [currentEdition, setCurrentEdition] = useState(null);
  const [pastEditions, setPastEditions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMagazine();
  }, []);

  const loadMagazine = async () => {
    try {
      const res = await fetch('http://localhost:5000/api/magazine');
      const data = await res.json();
      setCurrentEdition(data.current || null);
      setPastEditions(data.editions || []);
    } catch (error) {
      console.error('Error loading magazine:', error);
      setCurrentEdition(null);
      setPastEditions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="magazine-loading">
        <div className="loading-magazine">
          <div className="magazine-pages">
            <div className="page"></div>
            <div className="page"></div>
            <div className="page"></div>
          </div>
        </div>
        <p>Cargando revista...</p>
      </div>
    );
  }

  return (
    <div className="magazine-page">
      {/* Hero con la edición actual */}
      <section className="magazine-hero">
        <div className="hero-background">
          {currentEdition?.cover_image && (
            <img src={currentEdition.cover_image} alt="" className="hero-bg-image" />
          )}
          <div className="hero-overlay"></div>
        </div>
        
        <div className="hero-content">
          <div className="magazine-badge">
            <span className="badge-new">NUEVA EDICIÓN</span>
            <span className="badge-weekly">Semanal</span>
          </div>
          
          <div className="magazine-title-container">
            <span className="magazine-brand">GRAPHYRA</span>
            <h1 className="magazine-main-title">Magazine</h1>
          </div>
          
          {currentEdition && (
            <div className="current-edition-info">
              <span className="edition-number">Edición #{currentEdition.edition_number}</span>
              <span className="edition-date">
                <FiCalendar /> {formatDate(currentEdition.published_at)}
              </span>
            </div>
          )}
          
          <h2 className="edition-headline">{currentEdition?.title || 'Cargando...'}</h2>
          <p className="edition-subtitle">{currentEdition?.subtitle}</p>
          
          {currentEdition?.id && (
            <Link to={`/magazine/${currentEdition.id}`} className="btn-read-magazine">
              <FiBookOpen /> Leer esta edición
            </Link>
          )}
        </div>
        
        {/* Representación visual de la revista */}
        <div className="magazine-cover-3d">
          <div className="cover-wrapper">
            <div className="cover-front">
              {currentEdition?.cover_image && (
                <img src={currentEdition.cover_image} alt={currentEdition.title} />
              )}
              <div className="cover-overlay">
                <span className="cover-brand">GRAPHYRA</span>
                <span className="cover-edition">#{currentEdition?.edition_number}</span>
              </div>
            </div>
            <div className="cover-spine"></div>
            <div className="cover-pages">
              <div className="page"></div>
              <div className="page"></div>
              <div className="page"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Artículos de la edición actual */}
      {currentEdition?.articles?.length > 0 && (
        <section className="current-articles">
          <div className="section-header">
            <h2>En esta edición</h2>
            <p>Los artículos destacados de esta semana</p>
          </div>
          
          <div className="articles-list">
            {currentEdition.articles.map((article, index) => (
              <Link 
                key={article.id} 
                to={`/article/${getArticleSlug(article)}`}
                className={`article-item ${article.featured ? 'featured' : ''}`}
              >
                <span className="article-number">{String(index + 1).padStart(2, '0')}</span>
                <div className="article-content">
                  <h3>{article.title}</h3>
                  <span className="article-author">Por {article.author}</span>
                </div>
                {article.featured && (
                  <span className="featured-badge">
                    <FiStar /> Destacado
                  </span>
                )}
                <FiChevronRight className="article-arrow" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Suscripción */}
      <section className="magazine-subscribe">
        <div className="subscribe-content">
          <div className="subscribe-icon">📬</div>
          <h3>No te pierdas ninguna edición</h3>
          <p>Suscríbete y recibe la revista semanal en tu correo</p>
          <form className="subscribe-form">
            <input type="email" placeholder="tu@correo.com" />
            <button type="submit">Suscribirse</button>
          </form>
        </div>
      </section>

      {/* Ediciones anteriores */}
      <section className="past-editions">
        <div className="section-header">
          <h2>📚 Ediciones Anteriores</h2>
          <p>Explora nuestro archivo de publicaciones</p>
        </div>
        
        {pastEditions.length > 0 ? (
          <div className="editions-grid">
            {pastEditions.map(edition => (
              <Link 
                key={edition.id} 
                to={`/magazine/${edition.id}`}
                className="edition-card"
              >
                <div className="edition-cover">
                  <img src={edition.cover_image} alt={edition.title} />
                  <span className="edition-badge">#{edition.edition_number}</span>
                </div>
                <div className="edition-info">
                  <h4>{edition.title}</h4>
                  <span className="edition-date">{formatDate(edition.published_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="no-past-editions">
            <p>Esta es nuestra primera edición. ¡Pronto habrá más!</p>
          </div>
        )}
      </section>

      {/* Footer de marca */}
      <section className="magazine-footer">
        <div className="footer-brand">
          <span className="brand-name">GRAPHYRA Magazine</span>
          <span className="brand-tagline">Conocimiento que inspira</span>
        </div>
      </section>
    </div>
  );
};

export default Magazine;
